import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { DASTicketDetails } from '../../../../../api/types/dasTickets.types';

interface TicketBasicInfoProps {
  ticket: DASTicketDetails;
  /**
   * Whether to render the External Reference Number edit pencil.
   * Driven by the `edit-external-ref` action on the `request-status-lookup`
   * module (Role Permissions UI). Previously named `isAdmin` and hardcoded
   * to the das_admin role — renamed for clarity now that the gate is
   * configurable per role.
   */
  canEditExternalReference?: boolean;
  onEditExternalReference?: () => void;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const TicketBasicInfo: React.FC<TicketBasicInfoProps> = ({ ticket, canEditExternalReference, onEditExternalReference }) => {
  return (
    <div
      style={{
        background: 'var(--bg-secondary)',
        borderRadius: '0.5rem',
        padding: '1.25rem',
        border: '1px solid var(--border-color)',
      }}
    >
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
        Basic Information
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Ticket Number
          </div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {ticket.ticketNo}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            External Reference No
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {ticket.externalReferenceNo || '—'}
            </div>
            {canEditExternalReference && onEditExternalReference && (
              <button
                type="button"
                onClick={onEditExternalReference}
                title="Edit external reference"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                }}
              >
                <IconSystem.edit size={14} />
              </button>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Request Type
          </div>
          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {ticket.requestType}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Priority
          </div>
          <span
            style={{
              display: 'inline-block',
              padding: '0.25rem 0.625rem',
              background:
                ticket.priority === 'Urgent'
                  ? '#dc2626'
                  : ticket.priority === 'High'
                  ? '#f59e0b'
                  : ticket.priority === 'Medium'
                  ? '#3b82f6'
                  : '#64748b',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '0.25rem',
              textTransform: 'uppercase',
            }}
          >
            {ticket.priority}
          </span>
        </div>
        {ticket.requiredByDate && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Required By Date
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
              {new Date(ticket.requiredByDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </div>
          </div>
        )}
      </div>

      {/* Value */}
      {ticket.value !== undefined && ticket.value !== null && (
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 600 }}>
            Value
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary)' }}>
            {ticket.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      )}

      {/* Subject */}
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 600 }}>
          Subject
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>{ticket.subject}</div>
      </div>

      {/* Description */}
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 600 }}>
          Description
        </div>
        <div
          style={{
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            textAlign: 'justify',
          }}
        >
          {ticket.description || 'No description provided'}
        </div>
      </div>

      {/* Creator Information */}
      <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.75rem', fontWeight: 600 }}>
          Creator Information
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Created By
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
              {ticket.createdByUserName}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Created On
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
              {formatDate(ticket.createdAt)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketBasicInfo;
